import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  DirectionsRun,
  FiberManualRecord,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import PageHeader from './PageHeader';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import {
  getActivityCameras,
  getActivityFeatureSettings,
  startActivityCameraStream,
  stopActivityCameraStream,
  updateActivityFeatureSettings,
} from '../utils/api';
import { formatDateWithSeconds } from '../utils/dateUtils';

const formatIntervalValue = (value, fallback = '') => {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) {
      return String(parsed);
    }
  }
  return fallback;
};

const parseNonNegativeIntegerInput = (value, label) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === '') {
    throw new Error(`${label} is required.`);
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || !Number.isInteger(parsed) || parsed < 0) {
    throw new Error(`${label} must be a whole number (0 or greater).`);
  }
  return parsed;
};

const parseActivityReappearTimeoutInput = (value, label) => {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === '') {
    throw new Error(`${label} is required.`);
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed) || !Number.isInteger(parsed) || parsed < -1) {
    throw new Error(`${label} must be -1, 0, or a positive whole number.`);
  }
  return parsed;
};

const ActivitySettings = () => {
  const navigate = useNavigate();
  const { confirm } = useConfirmDialog();

  const [activityCameras, setActivityCameras] = useState([]);
  const [cameraActionLoading, setCameraActionLoading] = useState(false);
  const [cameraActionMessage, setCameraActionMessage] = useState('');

  const [activityRoundCooldown, setActivityRoundCooldown] = useState('30');
  const [activityReappearTimeout, setActivityReappearTimeout] = useState('0');
  const [activitySettingSaving, setActivitySettingSaving] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [activitySettingMetadata, setActivitySettingMetadata] = useState({
    updated_by: null,
    updated_by_name: null,
    updated_at: null,
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const applyActivitySettingMetadata = (data) => {
    setActivitySettingMetadata({
      updated_by: data?.updated_by || null,
      updated_by_name: data?.updated_by_name || null,
      updated_at: data?.updated_at || null,
    });
  };

  const loadActivityCameras = useCallback(async () => {
    try {
      const response = await getActivityCameras();
      const cameras = Array.isArray(response?.cameras) ? response.cameras : [];
      setActivityCameras(cameras);
      return cameras;
    } catch (_) {
      setActivityCameras([]);
      return [];
    }
  }, []);

  const loadActivityFeatureSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const data = await getActivityFeatureSettings();
      setActivityRoundCooldown(
        formatIntervalValue(data?.activity_round_cooldown_sec, '30'),
      );
      setActivityReappearTimeout(
        formatIntervalValue(data?.activity_reappear_timeout_sec, '0'),
      );
      applyActivitySettingMetadata(data);
    } catch (err) {
      showSnackbar(err.message || 'Unable to load activity settings', 'error');
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivityCameras();
    loadActivityFeatureSettings();
  }, [loadActivityCameras, loadActivityFeatureSettings]);

  useEffect(() => {
    const onStatusChanged = () => {
      loadActivityCameras();
    };
    window.addEventListener('activity-tracking-status-changed', onStatusChanged);
    return () => window.removeEventListener('activity-tracking-status-changed', onStatusChanged);
  }, [loadActivityCameras]);

  const handleStartCamera = async () => {
    setCameraActionLoading(true);
    try {
      if (activityCameras.length === 0) {
        setCameraActionMessage('No activity cameras configured');
        return;
      }
      const results = await Promise.allSettled(
        activityCameras.map((camera) => startActivityCameraStream(camera.camera_id))
      );
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const latestCameras = await loadActivityCameras();
      const isRunning = latestCameras.some((camera) => Boolean(camera?.stream_running));
      setCameraActionMessage(
        isRunning
          ? `Started activity tracking on ${successCount}/${activityCameras.length} camera(s)`
          : 'Start requested, but tracking is still off'
      );
      window.dispatchEvent(new CustomEvent('activity-tracking-status-changed'));
    } catch (e) {
      setCameraActionMessage(e?.message || 'Failed to start activity stream');
    } finally {
      setCameraActionLoading(false);
    }
  };

  const handleStopCamera = async () => {
    setCameraActionLoading(true);
    try {
      if (activityCameras.length === 0) {
        setCameraActionMessage('No activity cameras configured');
        return;
      }
      const results = await Promise.allSettled(
        activityCameras.map((camera) => stopActivityCameraStream(camera.camera_id))
      );
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const latestCameras = await loadActivityCameras();
      const isRunning = latestCameras.some((camera) => Boolean(camera?.stream_running));
      setCameraActionMessage(
        !isRunning
          ? `Stopped activity tracking on ${successCount}/${activityCameras.length} camera(s)`
          : 'Stop requested, but tracking is still on'
      );
      window.dispatchEvent(new CustomEvent('activity-tracking-status-changed'));
    } catch (e) {
      setCameraActionMessage(e?.message || 'Failed to stop activity stream');
    } finally {
      setCameraActionLoading(false);
    }
  };

  const runningCount = activityCameras.filter((camera) => camera.stream_running).length;
  const isTrackingRunning = runningCount > 0;

  const handleToggleActivityTracking = async () => {
    if (isTrackingRunning) {
      const approved = await confirm({
        title: 'Stop Activity Tracking',
        message: 'Stopping will pause activity capture. Continue?',
        confirmLabel: 'Stop',
        danger: true,
      });
      if (approved) {
        handleStopCamera();
      }
      return;
    }
    const approved = await confirm({
      title: 'Start Activity Tracking',
      message: 'Start activity tracking for configured cameras?',
      confirmLabel: 'Start',
      danger: false,
    });
    if (approved) {
      handleStartCamera();
    }
  };

  const handleSaveActivitySetting = async () => {
    let activityCooldownValue;
    let activityReappearValue;
    try {
      activityCooldownValue = parseNonNegativeIntegerInput(
        activityRoundCooldown,
        'Activity round cooldown',
      );
      activityReappearValue = parseActivityReappearTimeoutInput(
        activityReappearTimeout,
        'Activity reappearance timeout',
      );
    } catch (err) {
      showSnackbar(err.message, 'error');
      return;
    }

    const approved = await confirm({
      title: 'Confirm Save Activity Setting',
      message: 'Are you sure you want to save these activity setting?',
      confirmLabel: 'Confirm',
      danger: false,
    });
    if (!approved) return;

    setActivitySettingSaving(true);
    try {
      const response = await updateActivityFeatureSettings({
        activity_round_cooldown_sec: activityCooldownValue,
        activity_reappear_timeout_sec: activityReappearValue,
      });
      const nextCooldownValue =
        response?.activity_round_cooldown_sec != null
          ? response.activity_round_cooldown_sec
          : activityCooldownValue;
      const nextReappearValue =
        response?.activity_reappear_timeout_sec != null
          ? response.activity_reappear_timeout_sec
          : activityReappearValue;
      setActivityRoundCooldown(formatIntervalValue(nextCooldownValue, '30'));
      setActivityReappearTimeout(formatIntervalValue(nextReappearValue, '0'));
      applyActivitySettingMetadata(response);
      showSnackbar('Activity setting saved successfully', 'success');
    } catch (err) {
      showSnackbar(err.message || 'Failed to save activity setting', 'error');
    } finally {
      setActivitySettingSaving(false);
    }
  };

  return (
    <Container maxWidth="lg" disableGutters sx={{ px: { xs: 0, sm: 1 } }}>
      <PageHeader
        title="Activity Settings"
        subtitle="Control activity tracking and tune how often cameras scan and how people are logged"
      />

      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
          Activity Tracking
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Start or stop live activity capture on assigned ACTIVITY cameras, or open Camera Configuration to assign cameras.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ alignItems: { sm: 'center' }, flexWrap: 'wrap' }}>
          <Button
            variant={isTrackingRunning ? 'outlined' : 'contained'}
            color={isTrackingRunning ? 'warning' : 'primary'}
            onClick={handleToggleActivityTracking}
            disabled={cameraActionLoading}
            startIcon={
              isTrackingRunning ? (
                <FiberManualRecord sx={{ color: '#f97316', fontSize: 14 }} />
              ) : (
                <DirectionsRun />
              )
            }
            sx={
              isTrackingRunning
                ? {
                    borderColor: '#f97316',
                    color: '#f97316',
                    '&:hover': {
                      borderColor: '#ea580c',
                      backgroundColor: 'rgba(249, 115, 22, 0.08)',
                    },
                  }
                : undefined
            }
          >
            {isTrackingRunning ? 'Stop Activity Tracking' : 'Start Activity Tracking'}
          </Button>
          <Button
            variant="contained"
            onClick={() => navigate('/camera-streams')}
            disabled={cameraActionLoading}
          >
            Configure Activity Cameras
          </Button>
        </Stack>
        {cameraActionMessage ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {cameraActionMessage}
          </Typography>
        ) : null}
      </Box>

      <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
        Activity Setting
      </Typography>
      {(activitySettingMetadata.updated_by_name || activitySettingMetadata.updated_by) && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          <strong>Last updated by:</strong>{' '}
          {activitySettingMetadata.updated_by_name || activitySettingMetadata.updated_by}
          {activitySettingMetadata.updated_at &&
            ` (${formatDateWithSeconds(activitySettingMetadata.updated_at)})`}
        </Typography>
      )}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          mb: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Stored in activity tracking settings. Round cooldown applies after an activity round when a face is detected. Reappearance timeout deduplicates full-match logs on the same ACTIVITY camera.
        </Typography>
        {settingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Activity Round Cooldown (seconds)"
                  value={activityRoundCooldown}
                  onChange={(e) => setActivityRoundCooldown(e.target.value)}
                  fullWidth
                  placeholder="30"
                  InputProps={{ inputProps: { step: 1, min: 0 } }}
                  helperText="Applied after activity round if face is detected."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  label="Activity Reappearance Timeout (seconds)"
                  value={activityReappearTimeout}
                  onChange={(e) => setActivityReappearTimeout(e.target.value)}
                  fullWidth
                  placeholder="0"
                  InputProps={{ inputProps: { step: 1, min: -1 } }}
                  helperText="Minimum seconds before the same recognized person can be logged again on the same ACTIVITY camera. 0 = log every detection. -1 = log once per person per camera until cache reset."
                />
              </Grid>
            </Grid>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleSaveActivitySetting}
                disabled={activitySettingSaving}
                startIcon={activitySettingSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              >
                {activitySettingSaving ? 'Saving...' : 'Save Activity Setting'}
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ActivitySettings;
